// src/components/profile-detail/ProfileHeader.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Grid, Box, Typography, Stack, Chip, Button, Tooltip, useTheme
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PersonIcon from '@mui/icons-material/Person';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShareIcon from '@mui/icons-material/Share';

// Helper functions
export const getGenderFromData = (profile) => {
  if (profile.personal_details?.gender) return profile.personal_details.gender.toLowerCase();
  if (profile.gender) return profile.gender.toLowerCase();
  if (profile.profile_id) {
    const firstChar = profile.profile_id.charAt(0).toLowerCase();
    if (firstChar === 'm') return 'male';
    if (firstChar === 'f') return 'female';
  }
  return 'unknown';
};

// Custom hook for handling profile images
const useProfileImage = (profileId, gender = 'unknown') => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const getDefaultImage = useCallback(() => {
    const isFemale = gender === 'female' || gender === 'f';
    return `/whiz-public/profiles-images/default-${isFemale ? 'female' : 'male'}.webp`;
  }, [gender]);

  useEffect(() => {
    if (!profileId) {
      setImageSrc(getDefaultImage());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setImageError(false);
    
    const img = new Image();
    img.src = `/whiz-public/profiles-images/${profileId}.webp`;
    
    img.onload = () => {
      setImageSrc(img.src);
      setImageError(false);
      setIsLoading(false);
    };
    
    img.onerror = () => {
      setImageSrc(getDefaultImage());
      setImageError(true);
      setIsLoading(false);
    };
  }, [profileId, getDefaultImage]);

  return { imageSrc, isLoading, imageError };
};

const ProfileHeader = ({ profile, navigate }) => {
  const theme = useTheme();
  
  const gender = useMemo(() => getGenderFromData(profile), [profile]);
  const isFemale = useMemo(() => gender === 'female' || gender === 'f', [gender]);
  const isMale = useMemo(() => gender === 'male' || gender === 'm', [gender]);
  
  const { imageSrc, isLoading, imageError } = useProfileImage(profile.profile_id, gender);

  const getGenderIcon = useCallback(() => {
    if (isFemale) return <FemaleIcon />;
    if (isMale) return <MaleIcon />;
    return <PersonIcon />;
  }, [isFemale, isMale]);

  const getGenderText = useCallback(() => {
    if (isFemale) return 'Female';
    if (isMale) return 'Male';
    return 'Not specified';
  }, [isFemale, isMale]);

  const age = useMemo(() => {
    if (profile.age) return profile.age;
    return null;
  }, [profile.age]);

  // Format number for display with proper spacing - FIXED: Now handles numbers that already have country code
  const formatDisplayNumber = (fullNumber) => {
    if (!fullNumber) return '';
    
    // If number already has +, extract and format properly
    if (fullNumber.startsWith('+')) {
      // Extract country code (1-3 digits after +) and local number
      const match = fullNumber.match(/^\+(\d{1,3})(\d+)$/);
      if (match) {
        const countryCodeDigits = match[1];
        const localNumber = match[2];
        const countryCode = `+${countryCodeDigits}`;
        
        // Format based on local number length
        const len = localNumber.length;
        
        if (len === 10) {
          return `${countryCode} ${localNumber.slice(0, 3)} ${localNumber.slice(3, 6)} ${localNumber.slice(6)}`;
        } else if (len === 9) {
          if (localNumber.startsWith('04')) {
            return `${countryCode} ${localNumber.slice(0, 4)} ${localNumber.slice(4, 7)} ${localNumber.slice(7)}`;
          }
          return `${countryCode} ${localNumber.slice(0, 3)} ${localNumber.slice(3, 6)} ${localNumber.slice(6)}`;
        } else if (len === 8) {
          return `${countryCode} ${localNumber.slice(0, 4)} ${localNumber.slice(4)}`;
        } else {
          const groups = localNumber.match(/\d{1,3}/g) || [];
          return `${countryCode} ${groups.join(' ')}`;
        }
      }
    }
    
    return fullNumber;
  };

  const handleShareClick = useCallback((e) => {
    e.stopPropagation();
    const profileUrl = `${window.location.origin}/profile/${profile.profile_id}`;
    const message = `Check out ${profile.personal_details?.full_name || profile.full_name} (${age || '--'}y, ${getGenderText()}) on Noor-un-Nikah: ${profileUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  }, [profile, age, getGenderText]);

  // Get values directly from profile (already flattened by context)
  const guardianName = profile.guardian_name;
  const guardianRelation = profile.guardian_relation;
  
  // These already include country code
  const guardianContact = profile.guardian_contact; // "+61404694786"
  const guardianWhatsapp = profile.guardian_whatsapp; // "+61404694786"
  
  // Raw values and country codes for display
  const guardianCountryCode = profile.guardian_country_code; // "+614"
  const guardianWhatsappCountryCode = profile.guardian_whatsapp_country_code; // "+614"

  // Format numbers for display
  const displayContactNumber = guardianContact ? formatDisplayNumber(guardianContact) : '';
  const displayWhatsappNumber = guardianWhatsapp ? formatDisplayNumber(guardianWhatsapp) : '';

  // Extract digits for WhatsApp link (remove all non-digits)
  const whatsappDigits = guardianWhatsapp ? guardianWhatsapp.replace(/\D/g, '') : '';

  // Check if we have any contact info to display
  const hasContactInfo = guardianName || guardianContact || guardianWhatsapp;

  const verificationStatus = useMemo(() => {
    if (profile.verified?.cnic || profile.documents_verification?.cnic_verified) {
      return { cnic: true };
    }
    return null;
  }, [profile]);

  return (
    <>
      {/* Back Button and Share Button */}
      <Box sx={{ p: 3, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            color: 'primary.light',
            bgcolor: 'primary.alpha10',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            px: 3,
            py: 1.2,
            fontWeight: 600,
            transition: theme.transitions.create(['all']),
            '&:hover': {
              bgcolor: 'secondary.alpha20',
              borderColor: 'primary.light',
              transform: 'translateY(-2px)',
              boxShadow: 4
            }
          }}
        >
          Back to Profiles
        </Button>

        <Tooltip title="Share Profile via WhatsApp">
          <Button
            variant="outlined"
            size="medium"
            onClick={handleShareClick}
            startIcon={<ShareIcon />}
            sx={{
              color: 'success.main',
              borderColor: 'success.main',
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 600,
              transition: theme.transitions.create(['all']),
              '&:hover': {
                bgcolor: 'success.alpha10',
                borderColor: 'success.dark',
                color: 'success.dark',
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
          >
            Share Profile
          </Button>
        </Tooltip>
      </Box>

      {/* Header Content */}
      <Box sx={{
        p: { xs: 3, md: 4 },
        bgcolor: 'primary.alpha5',
        borderBottom: 1,
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: `linear-gradient(135deg, primary.alpha10 0%, secondary.alpha5 100%)`,
          zIndex: 0
        }
      }}>
        <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Profile Image */}
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Box
                sx={{
                  width: 200,
                  height: 260,
                  position: 'relative',
                  margin: '0 auto',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: 2,
                  borderColor: 'primary.alpha20',
                  boxShadow: 4,
                  transition: theme.transitions.create(['all']),
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: 8,
                    transform: 'scale(1.03)'
                  }
                }}
              >
                {(isLoading || imageError) && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: isFemale ? 'rgba(236, 64, 122, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2
                  }}>
                    {/* {isFemale ? <FemaleIcon /> : <MaleIcon />} // remove it */}
                  </Box>
                )}
                
                {imageSrc && (
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={profile.personal_details?.full_name || profile.full_name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      transition: 'transform 0.5s ease',
                      opacity: isLoading ? 0 : 1,
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                  />
                )}
                
                {verificationStatus?.cnic && (
                  <Box sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    zIndex: 2,
                    boxShadow: 2,
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <VerifiedIcon sx={{ fontSize: 14 }} />
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, gap: 1 }}>
                <Box sx={{ bgcolor: 'primary.alpha10', color: 'primary.light', p: '6px 16px', borderRadius: 5, border: 1, borderColor: 'divider', fontWeight: 600, fontSize: '0.9rem' }}>
                  ID: {profile.profile_id}
                </Box>
                <Box sx={{
                  bgcolor: isFemale ? 'rgba(236, 64, 122, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                  color: isFemale ? 'pink.700' : 'primary.main',
                  p: '4px 12px',
                  borderRadius: 4,
                  border: 1,
                  borderColor: isFemale ? 'rgba(236, 64, 122, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  {isFemale ? <FemaleIcon sx={{ fontSize: 14 }} /> : <MaleIcon sx={{ fontSize: 14 }} />}
                  {getGenderText()}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Profile Info */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h3" color="text.primary" sx={{ fontWeight: 800, mb: 2 }}>
                  {profile.personal_details?.full_name || profile.full_name || 'Unnamed Profile'}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
                  {age && <Chip label={`${age} years`} sx={{ bgcolor: 'primary.alpha10', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider' }} />}
                  <Chip icon={getGenderIcon()} label={getGenderText()} sx={{ bgcolor: 'primary.alpha10', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider', '& .MuiChip-icon': { color: 'primary.light' } }} />
                  {(profile.profession?.job_title || profile.job_title) && (
                    <Chip icon={<WorkIcon />} label={profile.profession?.job_title || profile.job_title} sx={{ bgcolor: 'primary.alpha10', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider', '& .MuiChip-icon': { color: 'primary.light' } }} />
                  )}
                  {(profile.education?.highest_degree || profile.highest_degree) && (
                    <Chip icon={<SchoolIcon />} label={profile.education?.highest_degree || profile.highest_degree} sx={{ bgcolor: 'primary.alpha10', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider', '& .MuiChip-icon': { color: 'primary.light' } }} />
                  )}
                  {(profile.residence?.city || profile.city) && (
                    <Chip icon={<LocationOnIcon />} label={profile.residence?.city || profile.city} sx={{ bgcolor: 'primary.alpha10', color: 'primary.light', fontWeight: 600, border: 1, borderColor: 'divider', '& .MuiChip-icon': { color: 'primary.light' } }} />
                  )}
                </Box>
              </Box>

              {/* Guardian Contact Section */}
              {hasContactInfo && (
                <Box sx={{ bgcolor: 'primary.alpha10', p: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <ContactPageIcon sx={{ color: 'primary.light', fontSize: 24 }} />
                    <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 700 }}>
                      Guardian Contact
                      {guardianRelation === 'self' && (
                        <Chip 
                          label="Self" 
                          size="small" 
                          sx={{ ml: 1, bgcolor: 'primary.alpha20', color: 'primary.light' }} 
                        />
                      )}
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {/* Guardian Name */}
                    {guardianName && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: 'primary.alpha5', height: '100%' }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.alpha20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PersonIcon sx={{ color: 'primary.light' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Guardian</Typography>
                            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                              {guardianName}
                            </Typography>
                            {guardianRelation && guardianRelation !== 'self' && (
                              <Typography variant="caption" color="text.secondary">
                                Relation: {guardianRelation}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* Contact Number */}
                    {guardianContact && (
                      <Grid item xs={12} sm={guardianName ? 6 : 12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: 'primary.alpha5', height: '100%' }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.alpha20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PhoneIcon sx={{ color: 'primary.light' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Contact Number</Typography>
                            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              {displayContactNumber}
                            </Typography>
                            {guardianCountryCode && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Country Code: {guardianCountryCode}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* WhatsApp Number */}
                    {guardianWhatsapp && (
                      <Grid item xs={12}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2, 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: '#25D36610',
                          border: '1px solid #25D36630'
                        }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: 2, 
                            bgcolor: '#25D36620',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            <WhatsAppIcon sx={{ color: '#25D366' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>WhatsApp</Typography>
                            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              {displayWhatsappNumber}
                            </Typography>
                            {guardianWhatsappCountryCode && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Country Code: {guardianWhatsappCountryCode}
                              </Typography>
                            )}
                          </Box>
                          {/* WhatsApp Chat Button */}
                          {whatsappDigits && (
                            <Button
                              variant="contained"
                              size="small"
                              href={`https://wa.me/${whatsappDigits}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                bgcolor: '#25D366',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: '#128C7E',
                                },
                                minWidth: '100px',
                                ml: 'auto'
                              }}
                            >
                              Chat
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ProfileHeader;